import React, { useState, useMemo } from 'react';

import styles from './Login.module.css';

import { QRCodeSVG } from 'qrcode.react';
import FormLayout from './FormLayout';

interface TOTPSetupFormProps {
  email: string;
  appName: string;
  totpSecret: string;
  totpUri: string;
  onSubmit: (code: string) => void;
  onCancel: () => void;
  loading: boolean;
  errorMessage: string;
  message: string;
}

const TOTPSetupForm: React.FC<TOTPSetupFormProps> = ({
  email,
  appName,
  totpSecret,
  totpUri,
  onSubmit,
  onCancel,
  loading,
  errorMessage,
  message
}) => {
  const [code, setCode] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);

  React.useEffect(() => {
    setIsValid(/^\d{6}$/.test(code));
  }, [code]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isValid) {
      return;
    }
    onSubmit(code);
  };

  // QR helper (fallback when details.getSetupUri() is unavailable)
  const otpAuthUrl = useMemo(() => {
    if ((!totpSecret && !totpUri) || !email) return '';
    if (totpUri) return totpUri;
    const issuer = encodeURIComponent(appName);
    const label = encodeURIComponent(`${appName}:${email}`);
    return `otpauth://totp/${label}?secret=${totpSecret}&issuer=${issuer}`;
  }, [totpSecret, totpUri, email, appName]);

  return (
    <FormLayout
      title="Setup 2 Factor Authentication"
      errorMessage={errorMessage}
      message={message}
    >
      <form onSubmit={handleSubmit}>
        <div>
          <div className={styles['totp-steps']}>
            <div>1. Open your authenticator App</div>
            <div>2. Add a new account</div>
            <div>3. Scan the QR Code</div>
          </div>

          {(totpSecret || otpAuthUrl) && (
            <>
              <div className={styles['totp-svg-display']} >
                {otpAuthUrl && (
                  <>
                    <div id="qr-code-container" style={{ margin: '20px 0', textAlign: 'center' }}>
                      <QRCodeSVG value={otpAuthUrl} size={175} />
                    </div>
                  </>
                )}

                {/*  No need to display the code at this time
                <div style={{ fontSize: '0.7em', color: '#666' }}>
                  {totpSecret || 'N/A'}
                </div>
                */}
              </div>
              <div className={styles['totp-steps']}>
                <div>4. Enter the code from your authenticator app</div>
              </div>
            </>
        )} 
        </div>

        <div>
          <div className={styles['form-field']}>
            <label htmlFor="totp-code">Verification Code (6 digits): <span className={styles['label-required-star']}>*</span></label>
            <input
              id="totp-code"
              data-testid="totp-code-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              required
            />
          </div>
        </div>
      </form>

      <div className={styles['login-action-container']}>
        <button
          type="submit"
          className={styles['login-submit-btn']}
          data-testid="verify-btn" disabled={loading || !isValid}
          onClick={handleSubmit}
        >
          Verify & Enable
        </button>
      </div>
    </FormLayout>
  );
};

export default TOTPSetupForm;
